import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import {
  fetchPwaTokenBalance,
  fetchPwaWalletBalance,
  fetchPwaAuthUser,
  swapPwa,
  type TelegramUser,
  type PwaAuthUser
} from '../../utils/telegramAuth';
import { supabase, TABLES, type SolanaTokenData } from '../../utils/supabase';
import { getRelayTokenPriceSolana } from '../../hooks/useRelayPrice';

type TradeMode = 'buy' | 'sell';

const PageContainer = styled.div`
  width: 100%;
  min-height: calc(100vh - 80px);
  padding: 16px 16px 100px 16px;
  box-sizing: border-box;
  color: #ffffff;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  background: #0a0a0a;
  padding: 20px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  gap: 16px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TokenBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, rgba(153, 69, 255, 0.2) 0%, rgba(20, 241, 149, 0.2) 100%);
  border: 1px solid rgba(153, 69, 255, 0.3);
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 12px;
  color: #14F195;
`;

const TokenLogo = styled.img`
  width: 18px;
  height: 18px;
  border-radius: 50%;
`;

const ModeToggle = styled.div`
  display: flex;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 4px;
`;

const ModeButton = styled.button<{ $active: boolean; $mode: TradeMode }>`
  flex: 1;
  padding: 12px 16px;
  background: ${props => props.$active 
    ? props.$mode === 'buy'
      ? 'linear-gradient(135deg, #14F195 0%, #10b981 100%)'
      : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
    : 'transparent'};
  border: none;
  border-radius: 8px;
  color: ${props => props.$active ? '#000000' : 'rgba(255, 255, 255, 0.6)'};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 12px 16px;
  gap: 12px;
`;

const BalanceText = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
`;

const Input = styled.input`
  width: 100%;
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  outline: none;
`;

const ActionButton = styled.button<{ $mode: TradeMode }>`
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  border: none;
  background: ${(props) =>
    props.$mode === 'buy'
      ? 'linear-gradient(135deg, #14F195 0%, #10b981 100%)'
      : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'};
  color: #000000;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const HelperText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`;

const OutputRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.75);
`;

const OutputValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #14F195;
`;

const ChipButton = styled.button<{ $active: boolean }>`
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid ${props => props.$active ? '#10b981' : 'rgba(255, 255, 255, 0.12)'};
  background: ${props => props.$active ? 'rgba(16, 185, 129, 0.15)' : 'transparent'};
  color: #ffffff;
  font-size: 12px;
  cursor: pointer;
`;

const AmountPresetRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const StatusLink = styled.a`
  color: #14F195;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const SlippageRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const SentrySwapPWA: React.FC = () => {
  const [telegramUser, setTelegramUser] = useState<PwaAuthUser | TelegramUser | null>(null);
  const [tokens, setTokens] = useState<SolanaTokenData[]>([]);
  const [selectedMint, setSelectedMint] = useState('');
  const [direction, setDirection] = useState<TradeMode>('buy');
  const [amountIn, setAmountIn] = useState('');
  const [slippage, setSlippage] = useState(0.5);
  const [status, setStatus] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<{ signature: string; amountOut?: number | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [solPriceUsd, setSolPriceUsd] = useState<number | null>(null);
  const [tokenPriceUsd, setTokenPriceUsd] = useState<number | null>(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [walletBalanceSol, setWalletBalanceSol] = useState<number | null>(null);
  const [tokenBalance, setTokenBalance] = useState<number | null>(null);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const user = await fetchPwaAuthUser();
      if (!cancelled) {
        setTelegramUser(user);
      }
      if (user && !cancelled) {
        const balance = await fetchPwaWalletBalance();
        if (!cancelled) {
          setWalletBalanceSol(balance?.balanceSol ?? null);
        }
      }

      try {
        const [solTokens, agentTokens] = await Promise.all([
          supabase
            .from(TABLES.TOKENS_SOL)
            .select('*')
            .order('created_at', { ascending: false })
            .limit(100),
          supabase
            .from(TABLES.TOKENS_SOL_AGENTS)
            .select('*')
            .order('created_at', { ascending: false })
            .limit(100)
        ]);

        if (solTokens.error) {
          throw solTokens.error;
        }
        if (agentTokens.error) {
          throw agentTokens.error;
        }

        if (!cancelled) {
          const mappedAgents = (agentTokens.data || []).map((token) => ({
            ...token,
            creator_username: token.agent_name || token.creator_username || token.moltbook_handle || 'Molting_CMI',
            is_agent: true
          })) as SolanaTokenData[];
          const merged = [...(solTokens.data || []), ...mappedAgents].sort((a, b) => {
            const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
            const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
            return bTime - aTime;
          });
          setTokens(merged);
        }
      } catch (loadError) {
        console.error('[SentrySwapPWA] Failed to load tokens:', loadError);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mintParam = params.get('mint');
    if (mintParam) {
      setSelectedMint(mintParam);
    }
  }, [location.search]);

  const selectedToken = useMemo(
    () => tokens.find((token) => token.mint_address === selectedMint),
    [tokens, selectedMint]
  );

  const tokensPerSol = solPriceUsd && tokenPriceUsd && tokenPriceUsd > 0
    ? solPriceUsd / tokenPriceUsd
    : null;

  const estimatedOutput = useMemo(() => {
    const amount = parseFloat(amountIn || '0');
    if (!amount || amount <= 0 || !tokensPerSol) return null;
    return direction === 'buy' ? amount * tokensPerSol : amount / tokensPerSol;
  }, [amountIn, direction, tokensPerSol]);

  const estimatedOutputDisplay = useMemo(() => {
    if (estimatedOutput === null) {
      return priceLoading ? 'Fetching price…' : '--';
    }
    return direction === 'buy'
      ? estimatedOutput.toLocaleString(undefined, { maximumFractionDigits: 0 })
      : estimatedOutput.toFixed(6);
  }, [estimatedOutput, priceLoading, direction]);

  const canSwap = useMemo(() => {
    const amount = Number(amountIn);
    if (!telegramUser || !selectedMint || !Number.isFinite(amount) || amount <= 0) return false;
    if (direction === 'buy' && walletBalanceSol !== null && amount > walletBalanceSol) return false;
    if (direction === 'sell' && tokenBalance !== null && amount > tokenBalance) return false;
    return true;
  }, [telegramUser, selectedMint, amountIn, direction, walletBalanceSol, tokenBalance]);

  useEffect(() => {
    let cancelled = false;
    const loadPrices = async () => {
      if (!selectedToken) return;
      setPriceLoading(true);
      try {
        const [solPrice, tokenPrice] = await Promise.all([
          getRelayTokenPriceSolana('So11111111111111111111111111111111111111112'),
          getRelayTokenPriceSolana(selectedToken.mint_address)
        ]);
        if (!cancelled) {
          setSolPriceUsd(solPrice);
          setTokenPriceUsd(tokenPrice);
        }
      } finally {
        if (!cancelled) {
          setPriceLoading(false);
        }
      }
    };
    loadPrices();
    const timer = window.setInterval(loadPrices, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [selectedToken?.mint_address]);

  useEffect(() => {
    let cancelled = false;
    const loadTokenBalance = async () => {
      if (!selectedMint || direction !== 'sell') {
        setTokenBalance(null);
        return;
      }
      try {
        const balance = await fetchPwaTokenBalance(selectedMint);
        if (!cancelled) {
          setTokenBalance(balance.amount ?? 0);
        }
      } catch {
        if (!cancelled) {
          setTokenBalance(0);
        }
      }
    };
    loadTokenBalance();
    return () => {
      cancelled = true;
    };
  }, [selectedMint, direction]);

  const handleAmountChange = (value: string) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setAmountIn(value);
    }
  };

  const handlePresetSelect = (value: number) => {
    setAmountIn(value.toString());
  };

  const handleSellPreset = (percentage: number) => {
    if (!tokenBalance || tokenBalance <= 0) return;
    const adjusted = percentage === 100
      ? Math.max(0, tokenBalance - 0.000001)
      : tokenBalance * (percentage / 100);
    const formatted = adjusted.toFixed(6).replace(/\.?0+$/, '');
    setAmountIn(formatted);
  };

  const handleSwap = async () => {
    if (!canSwap || !selectedToken) return;
    if (!selectedToken.pool_address) {
      setStatus('No Orca pool found for this token.');
      return;
    }
    setLoading(true);
    setStatus(null);
    setSuccessData(null);
    try {
      const numericAmount = Number(amountIn);

      const tokenDecimals = selectedToken.decimals ?? 9;
      const amountInBase = direction === 'buy'
        ? Math.floor(numericAmount * 1e9)
        : Math.floor(numericAmount * Math.pow(10, tokenDecimals));

      const response = await swapPwa({
        direction,
        mintAddress: selectedToken.mint_address,
        poolAddress: selectedToken.pool_address,
        amountIn: String(amountInBase),
        minAmountOut: '0'
      });

      setSuccessData({ signature: response.signature, amountOut: response.amountOut ?? null });
      setStatus('Success!');
      setAmountIn('');
      const balance = await fetchPwaWalletBalance();
      setWalletBalanceSol(balance?.balanceSol ?? null);
      if (direction === 'sell' && selectedMint) {
        const tokenBalanceResult = await fetchPwaTokenBalance(selectedMint);
        setTokenBalance(tokenBalanceResult.amount ?? null);
      }
    } catch (swapError: any) {
      setStatus(swapError?.message || 'Swap failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Card>
        <ModalHeader>
          <ModalTitle>
            Swap
            {selectedToken && (
              <TokenBadge>
                {selectedToken.logo_url && (
                  <TokenLogo src={selectedToken.logo_url} alt={selectedToken.symbol} />
                )}
                {selectedToken.symbol || selectedToken.name}
              </TokenBadge>
            )}
          </ModalTitle>
        </ModalHeader>
        {!telegramUser && <HelperText>Sign in with Telegram to trade.</HelperText>}
        <ModeToggle>
          <ModeButton type="button" $active={direction === 'buy'} $mode="buy" onClick={() => setDirection('buy')}>
            Buy
          </ModeButton>
          <ModeButton type="button" $active={direction === 'sell'} $mode="sell" onClick={() => setDirection('sell')}>
            Sell
          </ModeButton>
        </ModeToggle>

        <InputGroup>
          <LabelRow>
            <Label>Amount ({direction === 'buy' ? 'SOL' : selectedToken?.symbol || 'Token'})</Label>
            {direction === 'buy' && (
              <BalanceText>
                Available: {walletBalanceSol !== null ? `${walletBalanceSol.toFixed(4)} SOL` : '—'}
              </BalanceText>
            )}
            {direction === 'sell' && (
              <BalanceText>
                Available: {tokenBalance !== null ? `${tokenBalance.toFixed(6)} ${selectedToken?.symbol || ''}` : '—'}
                {tokenPriceUsd !== null && tokenBalance !== null
                  ? ` ($${(tokenBalance * tokenPriceUsd).toFixed(2)})`
                  : ''}
              </BalanceText>
            )}
          </LabelRow>
          <InputWrapper>
            <Input
              type="text"
              inputMode="decimal"
              placeholder="0.0"
              value={amountIn}
              onChange={(event) => handleAmountChange(event.target.value)}
            />
          </InputWrapper>
          {direction === 'buy' && (
            <AmountPresetRow>
              {[0.1, 0.5, 1, 5, 10].map((value) => (
                <ChipButton
                  key={value}
                  type="button"
                  $active={Number(amountIn) === value}
                  onClick={() => handlePresetSelect(value)}
                >
                  {value}
                </ChipButton>
              ))}
            </AmountPresetRow>
          )}
          {direction === 'sell' && (
            <AmountPresetRow>
              {[25, 50, 75, 100].map((value) => (
                <ChipButton
                  key={value}
                  type="button"
                  $active={false}
                  onClick={() => handleSellPreset(value)}
                >
                  {value === 100 ? 'Max' : `${value}%`}
                </ChipButton>
              ))}
            </AmountPresetRow>
          )}
        </InputGroup>

        <OutputRow>
          <span>Estimated {direction === 'buy' ? selectedToken?.symbol || 'Token' : 'SOL'}</span>
          <OutputValue>{estimatedOutputDisplay}</OutputValue>
        </OutputRow>

        <InputGroup>
          <Label>Slippage (%)</Label>
          <SlippageRow>
            {[0.5, 1, 2, 3].map((value) => (
              <ChipButton
                key={value}
                type="button"
                $active={slippage === value}
                onClick={() => setSlippage(value)}
              >
                {value}%
              </ChipButton>
            ))}
          </SlippageRow>
        </InputGroup>

        <ActionButton type="button" $mode={direction} disabled={!canSwap || loading} onClick={handleSwap}>
          {loading ? 'Swapping…' : direction === 'buy' ? 'Buy' : 'Sell'}
        </ActionButton>
        {status && <HelperText>{status}</HelperText>}
        {successData?.signature && (
          <HelperText>
            {direction === 'buy' && successData.amountOut !== null && successData.amountOut !== undefined
              ? `+${successData.amountOut.toLocaleString(undefined, { maximumFractionDigits: 6 })} ${selectedToken?.symbol || 'TOKEN'}`
              : null}
            {direction === 'buy' ? ' ' : ''}
            <StatusLink
              href={`https://solscan.io/tx/${successData.signature}`}
              target="_blank"
              rel="noreferrer"
            >
              View transaction
            </StatusLink>
          </HelperText>
        )}
      </Card>
    </PageContainer>
  );
};

export default SentrySwapPWA;
